import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';


import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import useCurrentUser from '../../hooks/useCurrentUser';
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import { referenceBackgroundConsentSchema, formatSSN, formatPhone, formatName } from "./application.validation";

import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";

const ReferenceBackgroundCheckAuthorization = ({ positionName, onClose }) => {
  const [consentId, setConsentId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");
  const [comments, setComments] = useState("");

  // useCurrentUser provides all session data without repeating localStorage parsing
  const { tenantId: defaultTenantId, userId: defaultUserId, companyName } = useCurrentUser();

  const methods = useForm({
    resolver: zodResolver(referenceBackgroundConsentSchema),
    defaultValues: {
      tenantId: resolveFormTenantId(defaultTenantId),
      userId: defaultUserId ?? undefined,
      employeeFullName: "",
      dateOfBirth: "",
      socialSecurityNumber: "",
      currentAddress: "",
      homePhone: "",
      cellPhone: "",
      applicantSignature: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      position: positionName || ""
    },
    mode: "onChange"
  });

  const { handleSubmit, setValue, watch, reset } = methods;
  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    // Ensure flag is a string. If second arg is an event, default to 'send_to_office'
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["dateOfBirth", "applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Ensure empty cellPhone is not sent
      if (!payload.cellPhone) {
        delete payload.cellPhone;
      }

      // This handles the "Save" button case where we want to persist the existing status/comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (consentId) {
        payload.consentId = consentId;
      }
      let response;
      if (consentId) {
        response = await request(`/reference-background-consents/${consentId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/reference-background-consents', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Reference Authorization processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName,
    user: currentUserData,
    isReadOnly,
    actualFromId,
    closeForm,
    handleSave,
    handleStatusAction,
    isAdmin
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeFullName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const currentUserEmail = currentUserData?.email || "Not provided";

  useEffect(() => {
    if (positionName) {
      setValue("position", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchConsentData = async () => {

      // Must wait for currentUserData to be populated to determine role
      if (!currentUserData) return;

      // If admin, we generally need a targetUserId to know who to look up.
      // If no targetUserId as admin, we likely can't do anything (unless there's a different logic).
      if (isAdmin && !targetUserId) return;

      try {

        // If we don't have an ID to fetch from, and we are a user (not admin), 
        // we should assume this is a new form and autofill from profile.
        if (!actualFromId && !targetUserId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeFullName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''} `.trim(),
              dateOfBirth: parseIsoDateToMDYUS(currentUserData.dateOfBirth),
              socialSecurityNumber: currentUserData.ssn || "",
              currentAddress: currentUserData.address || "",
              homePhone: currentUserData.mobileNumber || "",
              cellPhone: "",
              // cellPhone: currentUserData.mobileNumber || "",
              position: positionName || (currentUserData.role ? currentUserData.role.name : "") || ""
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          response = await request(`/reference-background-consents/${actualFromId}?userId=${targetUserId}`);
        } else {
          response = await request(`/reference-background-consents/${actualFromId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.consent) {
            data = response.result.consent;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            data = response.result.items.find(item => item.userId === parseInt(targetUserId));
          }
        }
        if (data) {
          setConsentId(data.id);
          setCurrentStatus(data.status || "");
          setComments(data.comments || "");
          reset({
            ...data,
            employeeFullName: data.employeeFullName || data.applicantName || "",
            dateOfBirth: parseIsoDateToMDYUS(data.dateOfBirth),
            ...getStandardizedSignatures(data, isAdmin),
            reviewedBySignature: data.reviewedBySignature || data.adminSignature || "",
            reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt || data.adminSignatureDate || data.reviewedBySignatureDate),
            reviewedBySignedBy: data.reviewedBySignedBy || "",
            comments: data.comments || "",
            socialSecurityNumber: data.socialSecurityNumber || (!isAdmin ? currentUserData.ssn : "") || ""
          });
        } else if (currentUserData && !isAdmin) {
          reset({
            tenantId: resolveFormTenantId(currentUserData?.tenantId),
            userId: currentUserData.id,
            employeeFullName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''} `.trim(),
            dateOfBirth: parseIsoDateToMDYUS(currentUserData.dateOfBirth),
            socialSecurityNumber: currentUserData.ssn || "",
            currentAddress: currentUserData.address || "",
            homePhone: currentUserData.mobileNumber || "",
            cellPhone: currentUserData.mobileNumber || "", // Also filling cellPhone as it's common
            position: positionName || (currentUserData.role ? currentUserData.role.name : "") || ""
          });
        }
      } catch (error) {
        console.error("Error fetching consent data:", error);
      }
    };
    fetchConsentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
  }, [targetUserId, reset, currentUserData, actualFromId]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl overflow-hidden">
        {/* Header Section - Clean & Functional */}
        <EvaluationFormHeader
          title="Reference & Background Check Authorization"
          subtitle="Please complete the form below"
          email={currentUserEmail}
        />

        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 p-8 min-h-[600px]"
          >
            <fieldset disabled={isReadOnly} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={comments} />
              <div className="space-y-6 animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EvaluationInput
                    name="employeeFullName"
                    label="Employee Full Name"
                    placeholder="Full Name"
                    isRequired
                    onInput={(e) => { e.target.value = formatName(e.target.value); }}
                  />
                  <InputDatePicker
                    formatSeparator="/"
                    name="dateOfBirth"
                    label="Date of Birth"
                    isRequired
                  />

                  <EvaluationInput
                    name="socialSecurityNumber"
                    label="Social Security Number"
                    placeholder="XXX-XX-XXXX"
                    isRequired
                    maxLength={11}
                    onInput={(e) => {
                      e.target.value = formatSSN(e.target.value);
                    }}
                  />
                  <EvaluationInput
                    name="currentAddress"
                    label="Current Address"
                    className="md:col-span-2"
                    placeholder="Street, City, State, Zip"
                    isRequired
                  />
                  <EvaluationInput
                    name="position"
                    label="Position Applied For"
                    readOnly
                    isRequired
                  />
                  <EvaluationInput
                    name="homePhone"
                    label="Home Phone"
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    isRequired
                    onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
                  />
                  <EvaluationInput
                    name="cellPhone"
                    label="Cell Phone"
                    placeholder="(XXX)XXX-XXXX"
                    maxLength={13}
                    onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
                  />
                </div>

                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mt-8">
                  Authorization Statement
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-sm text-gray-700 space-y-4">
                  <p>
                    I certify that the information provided by me is correct. I
                    understand that false or misleading statements made herein, in
                    connection with this application, may result in
                    disqualification or dismissal from employment. I hereby
                    authorize <strong>{companyName}</strong> and its agents to make a
                    thorough investigation of my past employment and activities,
                    including but not limited to verification of all data given on
                    this application, related papers, or interviews. I agree to
                    cooperate in such investigation and release all parties from
                    all liability for any damage that may result from furnishing
                    information to <strong>{companyName}</strong>. I authorize the
                    procurement of a consumer report in connection with my
                    application for employment and, if hired, periodically during
                    my employment. I understand that this consumer report may
                    include verification of my social security number, current and
                    previous residences, employment history, education background,
                    character references, drug testing, civil and criminal history
                    records from any criminal justice agency in any or all
                    federal, state, county jurisdictions, driving records, birth
                    records, and any other public records.
                  </p>
                  <p>
                    I further authorize any individual, company, firm,
                    corporation, or public agency (including the Social Security
                    Administration and law enforcement agencies) to divulge any
                    and all information, verbal or written, pertaining to me to{" "}
                    <strong>{companyName}</strong> or its authorized representative(s). I
                    agree to furnish any additional information and complete any
                    authorizations as may be required to complete this review. I
                    further authorize the complete release of any records or data,
                    including information received from other sources, which may
                    be in their files or which may be obtained from other sources.
                    This authorization and consent is given in advance of any such
                    investigation, disclosure, or receipt of information and is
                    intended to comply with any and all applicable federal and
                    state laws.
                  </p>
                  <p>
                    I hereby release, discharge, and exonerate <strong>{companyName}</strong>,
                    the Social Security Administration, and its agents,
                    officials, representatives, or assigned agencies (including
                    officers, employees, or related personnel), both individually
                    and collectively, from any and all liability for damages of
                    whatever kind, which may at any time result to me, my heirs,
                    family, or associates because of compliance with this
                    authorization and request to release information, or any
                    attempt to comply with it.
                  </p>
                </div>
              </div>
            </fieldset>

            <ApplicationFormFooter
              page={1}
              setPage={() => { }}
              totalPages={1}
              methods={methods}
              onSave={handleSave}
              onSubmit={(data, status) => onSubmit(data, status)}
              onStatusUpdate={handleStatusAction}
              isSignatureModalOpen={isSignatureModalOpen}
              openSignatureModal={openSignatureModal}
              closeSignatureModal={closeSignatureModal}
              handleSignatureApply={handleSignatureApply}
              signerName={signerName}
              requireSignatures={true}
              showSignatureSections={true}
              applicantName={watch("employeeFullName") || ""}
              applicantSignature={watch("applicantSignature") || ""}
              applicantSignedAt={watch("applicantSignedAt") || ""}
              reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
              reviewedBySignature={watch("reviewedBySignature") || ""}
              reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
              isReadOnly={isReadOnly}
              onClose={closeForm}
              currentStatus={currentStatus}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default ReferenceBackgroundCheckAuthorization;

ReferenceBackgroundCheckAuthorization.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func
};
